import { BasicTable } from "components/tables/Table";
import { useTheme } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { Row } from "react-table";
import { Grid } from "@mui/material";
import { PaginationState } from "types/common";
import { IFno } from "types/fno";
import api from "api/api";
import serverCall from "api/serverCall";

type Props = {
	fno: IFno[];
	page: PaginationState;
	loading: boolean;
	totalNumberOfFno: number;
};

function FnoSection({ page, fno, totalNumberOfFno, loading }: Props) {
	const theme = useTheme();

	const EditableCell = ({
		rowData,
		fieldName,
	}: {
		rowData: IFno;
		fieldName: keyof IFno;
	}) => {
		const [isEditing, setIsEditing] = useState(false);
		const [value, setValue] = useState(rowData?.[fieldName] ?? "");
		const [stockId, setStockId] = useState(rowData.stockId);

		useEffect(() => {
			setStockId(stockId);
		}, []);
		const handleSave = async (stockId: string) => {
			const updatedFno = fno.map((data: IFno) => {
				if (data.stockId === stockId) {
					return {
						...data,
						[fieldName]: value ?? "",
					};
				}
				return data;
			});
			setIsEditing(false);
			try {
				 await serverCall.post<any>(
					api.updateFnoInstruments(),
					updatedFno
				);
			} catch (error) {
				setValue(rowData?.[fieldName] ?? "");
				console.error(error);
			} finally {
				// setEquitiesLoading(false);
			}
		};

		return (
			<div
				onDoubleClick={() => {
					setIsEditing(true);
				}}
				style={{ minHeight: "24px", minWidth: "40px", cursor: "pointer" }}>
				{isEditing ? (
					<input
						value={value}
						onChange={(e) => setValue(e.target.value)}
						onBlur={() => handleSave(stockId)}
						onKeyDown={(e) => {
							if (e.key === "Enter") handleSave(stockId);
						}}
						autoFocus
						style={{ width: "100%" }}
					/>
				) : (
					<span>{value}</span>
				)}
			</div>
		);
	};

	const columns = useMemo(
		() => [
			{
				Header: "#",
				accessor: "id",
				Cell: ({ row }: { row: Row }) => {
					return <Grid sx={{ p: 0.125 }}>{row.index + 1}</Grid>;
				},
			},
			{
				Header: "Stock Id",
				accessor: "stockId",
				Cell: ({ row }: { row: Row }) => {
					const fno = row.original as IFno;
					return <>{fno?.stockId}</>;
				},
			},
			{
				Header: "Stock",
				accessor: "stockName",
				Cell: ({ row }: { row: Row }) => {
					const fno = row.original as IFno;
					return <EditableCell rowData={fno} fieldName={"stockName"} />;
				},
			},
			{
				Header: "Zerodha Trading Symbol",
				accessor: "zerodhaTradingSymbol",
				Cell: ({ row }: { row: Row }) => {
					const fno = row.original as IFno;
					return (
						<EditableCell rowData={fno} fieldName={"zerodhaTradingSymbol"} />
					);
				},
			},
			{
				Header: "Zerodha Instrument Token",
				accessor: "zerodhaInstrumentToken",
				Cell: ({ row }: { row: Row }) => {
					const fno = row.original as IFno;
					return (
						<EditableCell rowData={fno} fieldName={"zerodhaInstrumentToken"} />
					);
				},
			},
			{
				Header: "Upstox Trading Symbol",
				accessor: "upstoxTradingSymbol",
				Cell: ({ row }: { row: Row }) => {
					const fno = row.original as IFno;
					return (
						<EditableCell rowData={fno} fieldName={"upstoxTradingSymbol"} />
					);
				},
			},
			{
				Header: "Upstox Instrument Key",
				accessor: "upstoxInstrumentKey",
				Cell: ({ row }: { row: Row }) => {
					const fno = row.original as IFno;
					return (
						<EditableCell rowData={fno} fieldName={"upstoxInstrumentKey"} />
					);
				},
			},
			{
				Header: "Kotak Trading Symbol",
				accessor: "kotakTradingSymbol",
				Cell: ({ row }: { row: Row }) => {
					const fno = row.original as IFno;
					return (
						<EditableCell rowData={fno} fieldName={"kotakTradingSymbol"} />
					);
				},
			},
			{
				Header: "Kotak Instrument Token",
				accessor: "kotakInstrumentToken",
				Cell: ({ row }: { row: Row }) => {
					const fno = row.original as IFno;
					return (
						<EditableCell rowData={fno} fieldName={"kotakInstrumentToken"} />
					);
				},
			},
			{
				Header: "Fyers Trading Symbol",
				accessor: "fyersTradingSymbol",
				Cell: ({ row }: { row: Row }) => {
					const fno = row.original as IFno;
					return (
						<EditableCell rowData={fno} fieldName={"fyersTradingSymbol"} />
					);
				},
			},
			{
				Header: "Five Paisa Scrip Data",
				accessor: "fivePaisaScripData",
				Cell: ({ row }: { row: Row }) => {
					const fno = row.original as IFno;
					return (
						<EditableCell rowData={fno} fieldName={"fivePaisaScripData"} />
					);
				},
			},
			{
				Header: "Angel Trading Symbol",
				accessor: "angelTradingSymbol",
				Cell: ({ row }: { row: Row }) => {
					const fno = row.original as IFno;
					return (
						<EditableCell rowData={fno} fieldName={"angelTradingSymbol"} />
					);
				},
			},
			{
				Header: "Angel Symbol Token",
				accessor: "angelSymbolToken",
				Cell: ({ row }: { row: Row }) => {
					const fno = row.original as IFno;
					return <EditableCell rowData={fno} fieldName={"angelSymbolToken"} />;
				},
			},
			{
				Header: "ICICI Stock Code",
				accessor: "iciciStockCode",
				Cell: ({ row }: { row: Row }) => {
					const fno = row.original as IFno;
					return <EditableCell rowData={fno} fieldName={"iciciStockCode"} />;
				},
			},
		],
		[theme, fno]
	);

	return (
		<>
			<BasicTable
				columns={columns}
				data={fno ?? []}
				title={`Fno:(${totalNumberOfFno})`}
				isLoading={loading}
				loadingText="Loading..."
				emptyMessage="No fno found..."
			/>
		</>
	);
}

export default FnoSection;
